/**
 * The top-bar customer selector: every page and every action works on the
 * customer chosen here. One customer shows as a plain label; none shows a
 * warning the administrator has to act on.
 */
import { useTranslation } from "react-i18next";
import { Box, Chip, FormControl, MenuItem, Select, Tooltip } from "@mui/material";
import BusinessIcon from "@mui/icons-material/Business";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useCustomer } from "../hooks/useCustomer";

/** Editing or viewing — an icon in the top bar, the words on hover. */
export function EditModeIcon() {
  const { t } = useTranslation();
  const { current, edit } = useCustomer();
  if (!current || !edit.known) return null;
  const title = edit.canEdit
    ? t("customer.editingHint")
    : t("customer.viewingHint", { name: edit.editor ?? "" });
  return (
    <Tooltip title={title}>
      <Box
        component="span"
        role="img"
        aria-label={title}
        sx={{ display: "inline-flex", alignItems: "center", px: 1 }}
      >
        {edit.canEdit ? (
          <EditIcon fontSize="small" color="success" />
        ) : (
          <VisibilityIcon fontSize="small" color="warning" />
        )}
      </Box>
    </Tooltip>
  );
}

export function CustomerSelect() {
  const { t } = useTranslation();
  const { customers, current, loading, select } = useCustomer();

  if (loading && customers.length === 0) return null;
  if (customers.length === 0) {
    return (
      <Tooltip title={t("customer.noneHint")}>
        <Chip
          icon={<BusinessIcon />}
          color="warning"
          variant="outlined"
          size="small"
          label={t("customer.none")}
        />
      </Tooltip>
    );
  }
  if (customers.length === 1) {
    return (
      <Tooltip title={t("customer.current")}>
        <Chip
          icon={<BusinessIcon />}
          variant="outlined"
          size="small"
          label={customers[0].name}
          sx={{ fontWeight: 600 }}
        />
      </Tooltip>
    );
  }
  return (
    <FormControl size="small" sx={{ minWidth: 200 }}>
      <Select
        value={current?.id ?? ""}
        displayEmpty
        onChange={(e) => select(String(e.target.value))}
        aria-label={t("customer.select")}
        renderValue={(value) =>
          value ? (customers.find((c) => c.id === value)?.name ?? "") : t("customer.choose")
        }
        startAdornment={<BusinessIcon fontSize="small" sx={{ mr: 1, color: "text.secondary" }} />}
      >
        {customers.map((c) => (
          <MenuItem key={c.id} value={c.id}>
            {c.name}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}
